<template>
  <img
    v-if="shouldPreloadImage && !imageLoaded"
    :src="currentBanner?.image"
    class="hidden"
    @load="onImageLoad"
    @error="onImageError"
  />
  
  <Teleport to="body">
    <transition name="modal">
      <div v-if="canShowBanner" class="fixed top-0 left-0 w-full h-screen z-[9998] bg-black bg-opacity-10 flex items-center justify-center transition-all">
        <transition name="image">
          <div ref="imageRef" class="max-w-5xl 2xl:max-w-7xl w-full aspect-auto rounded-md relative overflow-hidden transition-all" v-if="canShowBanner">
            <a @click="openInfo" class="cursor-pointer"><img class="w-full" :src="currentBanner?.image" alt=""></a>
            <div @click.stop="closeAction" class="absolute cursor-pointer bg-white px-2 rounded-full top-4 right-4">x</div>
            <div class="absolute bottom-3 right-4 z-[9999]">
              <EcxCheckBoxSimple
                :id="currentBanner?.id || 0"
                :name="t('components.global_banner.dont_see_more')"
                description=""
                :input-props="{ id: currentBanner?.id.toString() || '', name: 'bannerCheckbox' }"
                :field-checked="false"
              />
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </Teleport>
</template>

<script lang="ts" setup>
  import { storeToRefs } from 'pinia';
  import { useForm } from 'vee-validate';
  import { useI18n } from '@/composables/useI18n';
  import { useBannerStore } from '@/stores/banners';
  import { ref, watch, onMounted, computed } from 'vue';
  import { useClickOutside } from '@ecx/ui';
  import EcxCheckBoxSimple from '@/components/form/EcxCheckBoxSimple.vue';

  const openImage = ref(false);
  const imageRef = ref();
  const imageLoaded = ref(false);
  const loadTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
  const { values } = useForm();
  const { t } = useI18n();

  const store = useBannerStore();
  const { fetchPopupBanner, closeModal, setAlreadySeen } = store;
  const { isModalOpen, currentBanner, alreadySeenToday } = storeToRefs(store);

  onMounted(() => {
    fetchPopupBanner();
  });

  const startLoadTimeout = () => {
    clearLoadTimeout();
    loadTimeout.value = setTimeout(() => {
      if (!imageLoaded.value) {
        closeModal();
      }
    }, 2000);
  };

  const clearLoadTimeout = () => {
    if (loadTimeout.value) {
      clearTimeout(loadTimeout.value);
      loadTimeout.value = null;
    }
  };

  const openInfo = () => {
    window.open(currentBanner.value?.redirect_link);
    closeModal();
  };

  const shouldPreloadImage = computed(() => 
    isModalOpen.value && !alreadySeenToday.value && !!currentBanner.value?.image
  );

  const canShowBanner = computed(() => shouldPreloadImage.value && imageLoaded.value);

  const onImageLoad = () => {
    clearLoadTimeout();
    imageLoaded.value = true;
  };

  const onImageError = () => {
    clearLoadTimeout();
    imageLoaded.value = false;
    closeModal();
  };

  watch(() => currentBanner.value?.id, () => {
    imageLoaded.value = false;
  });

  watch(() => currentBanner.value?.image, () => {
    imageLoaded.value = false;
  });

  watch(shouldPreloadImage, (newValue) => {
    if (newValue && !imageLoaded.value) {
      startLoadTimeout();
    } else {
      clearLoadTimeout();
    }
  }, { immediate: true });

  const closeAction = () => {
    clearLoadTimeout();
    openImage.value = false;
    imageLoaded.value = false;
    closeModal();
  };

  watch(canShowBanner, () => { if(canShowBanner.value) setTimeout(() => {
    openImage.value = true;
  }, 400); });

  useClickOutside(imageRef, () => { 
    clearLoadTimeout();
    openImage.value = false;
    imageLoaded.value = false;
    closeModal();
  });

  watch(
    () => values.bannerCheckbox,
    () => {
      if(openImage.value) {
        setAlreadySeen(!!values.bannerCheckbox);
      }
    },
  );
</script>

<style scoped>
  .modal-enter-active,
  .modal-leave-active {
    opacity: 0;;
    transition: all .25s ease;
  }

  .modal-enter-from,
  .modal-leave-to{
    transform: scale(1.1);
  }

  .image-enter-active,
  .image-leave-active {
    transform: translateY(0px);
    transition: all .25s ease;
  }

  .image-enter-from,
  .image-leave-to{
    opacity: 0;
    transform: translateY(100px);
  }
</style>